import { useRef } from 'react';

import { setLinksDateRange, setLinksPages, setLinksText, useBatchedStats } from '../hooks/useBatchedStats';
import { ExportToolbar } from './ExportToolbar';
import { LinkInputForm } from './LinkInputForm';
import { StatsTable } from './StatsTable';

export function LinkStatisticsTab(): JSX.Element {
  const {
    results,
    errors,
    originalUrls,
    progress,
    isPending,
    isPaused,
    pauseReason,
    elapsedSeconds,
    displayPercent,
    startBatch,
    cancel,
    text,
    dateRange,
    pages,
  } = useBatchedStats();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  function handleFormSubmit(
    urls: string[],
    dateFrom?: string,
    dateTo?: string,
    parsedPages?: string[][][],
  ): void {
    if (parsedPages) setLinksPages(parsedPages);
    startBatch(urls, dateFrom, dateTo);
  }

  async function handleExportPdf(): Promise<void> {
    if (results.length === 0 || !tableContainerRef.current) return;
    const { exportElementToPdf } = await import('../lib/export-pdf');
    await exportElementToPdf(tableContainerRef.current, 'statistics');
  }

  const hasData = results.length > 0 || errors.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <LinkInputForm
        onSubmit={handleFormSubmit}
        onCancel={cancel}
        isPending={isPending}
        isPaused={isPaused}
        pauseReason={pauseReason}
        progress={progress}
        elapsedSeconds={elapsedSeconds}
        displayPercent={displayPercent}
        text={text}
        onTextChange={setLinksText}
        dateRange={dateRange}
        onDateRangeChange={setLinksDateRange}
      />

      {(hasData || isPending) && (
        <div className="flex flex-col gap-4">
          {results.length > 0 && !isPending && (
            <ExportToolbar data={results} onExportPdf={handleExportPdf} dateRange={dateRange} pages={pages} />
          )}
          <div ref={tableContainerRef}>
            <StatsTable
              results={results}
              errors={errors}
              originalUrls={originalUrls}
              isLoading={isPending && results.length === 0}
            />
          </div>
        </div>
      )}
    </div>
  );
}
